import { useShallow } from 'zustand/react/shallow';
import { useWindowsStore } from '../../store/windowsStore';
import Window from '../Window/Window';
import {
  FiUser, FiCode, FiCpu, FiBriefcase, FiAward, FiMail,
  FiTerminal, FiMusic, FiTrash2,
} from 'react-icons/fi';

const ICONS = {
  user: FiUser,
  code: FiCode,
  cpu: FiCpu,
  briefcase: FiBriefcase,
  award: FiAward,
  mail: FiMail,
  terminal: FiTerminal,
  music: FiMusic,
  trash: FiTrash2,
};

const TONE_DEFAULT = {
  about: 'cyan',
  projects: 'violet',
  skills: 'violet',
  experience: 'cyan',
  certificates: 'green',
  contact: 'pink',
  terminal: 'green',
  lofi: 'violet',
  trash: 'green',
};

/**
 * ManagedWindow — wrapper que conecta Window genérico ao windowsStore.
 * Selector usa useShallow + primitivos — siblings não re-renderizam.
 */
export default function ManagedWindow({ id, children }) {
  // Primitivos individuais — Object.is estável, sem loop de re-render.
  // ATENÇÃO: selectors que retornam objetos causam React #185 (infinite loop).
  const x = useWindowsStore((s) => s.windows[id]?.x);
  const y = useWindowsStore((s) => s.windows[id]?.y);
  const w = useWindowsStore((s) => s.windows[id]?.w);
  const h = useWindowsStore((s) => s.windows[id]?.h);

  const isOpen = useWindowsStore((s) => s.windows[id]?.isOpen);
  const isMinimized = useWindowsStore((s) => s.windows[id]?.isMinimized);
  const isMaximized = useWindowsStore((s) => s.windows[id]?.isMaximized);
  const z = useWindowsStore((s) => s.windows[id]?.z);
  const pinned = useWindowsStore((s) => !!s.windows[id]?.pinned);
  const title = useWindowsStore((s) => s.windows[id]?.title);
  const tone = useWindowsStore((s) => s.windows[id]?.tone || TONE_DEFAULT[id] || 'cyan');
  const iconName = useWindowsStore((s) => s.windows[id]?.icon);

  const closeWindow = useWindowsStore((s) => s.closeWindow);
  const minimizeWindow = useWindowsStore((s) => s.minimizeWindow);
  const toggleMaximize = useWindowsStore((s) => s.toggleMaximize);
  const focusWindow = useWindowsStore((s) => s.focusWindow);
  const moveWindow = useWindowsStore((s) => s.moveWindow);
  const resizeWindow = useWindowsStore((s) => s.resizeWindow);

  if (x === undefined || y === undefined || w === undefined || h === undefined) return null;
  if (!title) return null;

  const Icon = ICONS[iconName] || FiCode;

  return (
    <Window
      id={id}
      title={title}
      icon={<Icon />}
      tone={tone}
      isOpen={isOpen}
      isMinimized={isMinimized}
      isMaximized={isMaximized}
      zIndex={z}
      position={{ x, y }}
      size={{ width: w, height: h }}
      minSize={{ width: 380, height: 240 }}
      closable={!pinned}
      minimizable={!pinned}
      maximizable={!pinned}
      onClose={closeWindow}
      onMinimize={minimizeWindow}
      onMaximize={toggleMaximize}
      onFocus={focusWindow}
      onMove={moveWindow}
      onResize={resizeWindow}
    >
      {children}
    </Window>
  );
}
